import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { Loader } from 'components/atoms/Loader';
import { Fade, Grid, IconButton, Modal, Typography } from '@mui/material';
import { useTranslations } from 'hooks/useTranslations';
import {
  CustomModalRoot,
  ModalContent,
  ModalControlButton,
  ModalControlContent,
  ModalControlTopContent,
  ModalImage,
  ModalImageContent,
  ModalimageThumbnails,
  ModalimageThumbnailsContainer,
  ModalThumbnailsContainer,
  ModalThumbnailsSubContainer,
} from './styles';
import Icon from 'components/atoms/Icon/Icon';
import { EIconTypes } from 'constants/Icons';
import { colors } from 'theme/colors';
import { ESize, IEntryData, IPhoto, IPhotoFile, IPhotoUpload } from 'models/galery';
import { downloadGalleryPreview, getGalleryPreview } from 'requests/gallery';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import { downloadUrlAsFile } from 'utils/files';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.2;

interface ImodalImages extends PropsWithChildren {
  open: boolean;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  currentPosition: number;
  setCurrentPosition: (n: number) => void;
  galleryData: IEntryData[];
  imageThumbnails: IPhotoFile;
  getPhotoThumbnail?: (id: string) => void;
  imageFilesInitial?: IPhotoFile;
  sizeFullImage: ESize;
  downloadButton?: boolean;
}

export const ModalImages = ({
  open,
  onClose,
  onPrev,
  onNext,
  currentPosition,
  setCurrentPosition,
  galleryData,
  imageThumbnails,
  getPhotoThumbnail,
  imageFilesInitial,
  sizeFullImage,
  downloadButton = true,
}: ImodalImages) => {
  const { t } = useTranslations();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingThumbnails, setIsLoadingThumnails] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const isDragging = useRef(false);
  const start = useRef({ x: 0, y: 0 });

  const [url, setUrl] = useState<string>('');
  const [currentImageData, setCurrentImageData] = useState<IEntryData>();
  const [prevImageData, setPrevImageData] = useState<IEntryData>();
  const [nextImageData, setNextImageData] = useState<IEntryData>();
  const [imageFiles, setImageFiles] = useState<IPhotoFile>({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (Object.keys(imageThumbnails).length >= galleryData.length) {
      setIsLoadingThumnails(false);
    }
  }, [imageThumbnails]);

  useEffect(() => {
    setImageFiles((prev) => ({
      ...imageFilesInitial,
      ...prev,
    }));
  }, [imageFilesInitial]);

  useEffect(() => {
    setLoaded(false);
    setCurrentImageData(
      galleryData.find((data) => Number(data.position) === currentPosition - 1),
    );
    if (currentPosition > 1) {
      setPrevImageData(
        galleryData.find((data) => Number(data.position) === currentPosition - 2),
      );
    }
    if (currentPosition < galleryData.length) {
      setNextImageData(galleryData.find((data) => Number(data.position) === currentPosition));
    }
  }, [currentPosition, galleryData]);

  const getPhoto = async (imgId: string) => {
    const photo = await getGalleryPreview(imgId, sizeFullImage);
    if (photo) {
      setImageFiles((prev) => ({
        ...prev,
        [imgId]: photo,
      }));
    }
  };

  const getPrevNextPhoto = () => {
    (async function getPhotoData() {
      if (nextImageData && nextImageData.uniqueId && !imageFiles[nextImageData.uniqueId]) {
        await getPhoto(nextImageData.uniqueId);
      }
      if (prevImageData && prevImageData.uniqueId && !imageFiles[prevImageData.uniqueId]) {
        await getPhoto(prevImageData.uniqueId);
      }
    })();
  };

  useEffect(() => {
    (async function getPhotoData() {
      if (currentImageData?.uniqueId && open) {
        if (imageFiles[currentImageData?.uniqueId]) {
          if (typeof imageFiles[currentImageData?.uniqueId] === 'string') {
            setUrl(imageFiles[currentImageData?.uniqueId] as string);
          } else {
            setUrl(URL.createObjectURL(imageFiles[currentImageData?.uniqueId] as File));
          }
          setIsLoading(false);
        } else {
          setIsLoading(true);
          await getPhoto(currentImageData?.uniqueId);
        }
        getPrevNextPhoto();
      }
    })();
  }, [currentImageData, imageFiles, open]);

  useEffect(() => {
    if (
      open &&
      getPhotoThumbnail &&
      galleryData.length !== Object.keys(imageThumbnails).length
    ) {
      galleryData.map(async (data, i) => {
        if (!imageThumbnails[data.uniqueId]) {
          getPhotoThumbnail(data.uniqueId);
        }
      });
    }
  }, [open]);

  const resetZoom = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  const onClickClose = () => {
    resetZoom();
    onClose();
  };
  const onClickPrev = () => {
    resetZoom();
    onPrev();
  };
  const onClickNext = () => {
    resetZoom();
    onNext();
  };
  const onClickThumbnail = (position: number) => {
    resetZoom();
    setCurrentPosition(position + 1);
  };

  const handleMouseDown = (e: any) => {
    if (zoom === 1) return;
    isDragging.current = true;
    start.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };

  const handleMouseMove = (e: any) => {
    if (!isDragging.current) return;
    setPosition({
      x: e.clientX - start.current.x,
      y: e.clientY - start.current.y,
    });
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleZoomIn = () => {
    setZoom((z) => Math.min(z + ZOOM_STEP, MAX_ZOOM));
  };

  const handleZoomOut = () => {
    setZoom((z) => {
      const next = Math.max(z - ZOOM_STEP, MIN_ZOOM);
      if (next === MIN_ZOOM) {
        setPosition({ x: 0, y: 0 });
      }
      return next;
    });
  };

  const handleDownload = async () => {
    if (currentImageData?.uniqueId && currentImageData?.filename) {
      try {
        const url = await downloadGalleryPreview(currentImageData?.uniqueId, sizeFullImage);
        if (url) {
          downloadUrlAsFile(url, currentImageData?.filename);
        }
      } catch {}
    }
  };

  const getImageSrc = (id: string): string => {
    if (imageThumbnails && imageThumbnails[id]) {
      if (typeof imageThumbnails[id] === 'string') {
        return imageThumbnails[id];
      } else {
        return URL.createObjectURL(imageThumbnails[id]);
      }
    }
    return '';
  };

  return (
    <CustomModalRoot style={{ width: open ? '100%' : '0', height: open ? '100%' : '0' }}>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={open}
        onClose={onClickClose}
        closeAfterTransition
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Fade in={open}>
          <ModalContent>
            <ModalControlTopContent>
              <Grid container sx={{ gap: 1 }}>
                <IconButton onClick={handleZoomIn}>
                  <Icon type={EIconTypes.zoomIn} fontSize={24} />
                </IconButton>
                <IconButton onClick={handleZoomOut}>
                  <Icon type={EIconTypes.zoomOut} fontSize={24} />
                </IconButton>
                {downloadButton && (
                  <IconButton onClick={handleDownload}>
                    <Icon type={EIconTypes.download} fontSize={24} />
                  </IconButton>
                )}
                {currentPosition === 1 && (
                  <div style={{ padding: '5px 0' }}>
                    <Chips
                      text={t('images.mainPhoto.label')}
                      variant={EChipsVariants.primary}
                    />
                  </div>
                )}
              </Grid>
              <IconButton onClick={onClickClose}>
                <Icon type={EIconTypes.close} fontSize={24} />
              </IconButton>
            </ModalControlTopContent>
            <ModalImageContent
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              <ModalImage zoom={zoom} x={position.x} y={position.y}>
                {isLoading ? (
                  <Loader isLoading={true} />
                ) : (
                  <>
                    <Loader isLoading={!loaded} />
                    <img
                      alt={`photo_full_size_${currentPosition}`}
                      src={url}
                      style={{ display: loaded ? 'block' : 'none' }}
                      onLoad={() => setLoaded(true)}
                    />
                  </>
                )}
              </ModalImage>
            </ModalImageContent>
            <ModalThumbnailsContainer>
              <ModalThumbnailsSubContainer>
                {isLoadingThumbnails && (
                  <div style={{ width: '100%', position: 'absolute' }}>
                    <Loader isLoading />
                  </div>
                )}
                {galleryData?.map((data) => {
                  const srcThumb = getImageSrc(data.uniqueId);
                  return (
                    <ModalimageThumbnailsContainer
                      onClick={() => onClickThumbnail(Number(data.position))}
                    >
                      <ModalimageThumbnails
                        selected={Number(data.position) === currentPosition - 1}
                      >
                        {srcThumb && <img alt={`photo_thumbnail_size`} src={srcThumb} />}
                      </ModalimageThumbnails>
                    </ModalimageThumbnailsContainer>
                  );
                })}
              </ModalThumbnailsSubContainer>
            </ModalThumbnailsContainer>
            <ModalControlContent>
              <ModalControlButton onClick={onClickPrev}>
                <Icon type={EIconTypes.left} color={colors.primary500} />
              </ModalControlButton>
              <div style={{ display: 'flex', gap: '16px' }}>
                <Typography variant="subtitle2" sx={{ color: colors.primary900 }}>
                  {`${currentPosition}/${galleryData.length}`}
                </Typography>
                <Typography variant="body2" sx={{ color: colors.primary900 }}>
                  {currentImageData?.filename}
                </Typography>
              </div>
              <ModalControlButton onClick={onClickNext}>
                <Icon type={EIconTypes.right} color={colors.primary500} />
              </ModalControlButton>
            </ModalControlContent>
          </ModalContent>
        </Fade>
      </Modal>
    </CustomModalRoot>
  );
};
